import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Request from '../../../../../helpers/request'
import * as url from '../../../../../helpers/url_helper'
import ModulesView from './ModulesView'
import AppliancesView from './AppliancesView'
import ManualControlsView from './ManualControlsView'

type RoomView = 'modules' | 'appliances' | 'manual-controls'

const VIEWS: { key: RoomView; label: string }[] = [
    { key: 'modules', label: 'Modules' },
    { key: 'appliances', label: 'Appliances' },
    { key: 'manual-controls', label: 'Manual Controls' },
]

const RoomPage = () => {
    const { userId, placeId, roomId } = useParams()
    const [activeView, setActiveView] = useState<RoomView>('modules')
    const [loading, setLoading] = useState(true)
    const [roomName, setRoomName] = useState('')
    // Hold place-wide data; views slice to room as needed. ModulesView needs
    // cross-room access so it can show a Backup's Master even when the Master
    // lives in a different room.
    const [allPcbs, setAllPcbs] = useState<any[]>([])
    const [allRelays, setAllRelays] = useState<any[]>([])
    const [allSwitches, setAllSwitches] = useState<any[]>([])
    const [allRooms, setAllRooms] = useState<any[]>([])
    const [allDevices, setAllDevices] = useState<any[]>([])

    useEffect(() => {
        setLoading(true)
        Request.post(url.GET_USERS_DETAIL_URL, { userId, roomId: roomId || '' })
            .then((res: any) => {
                const data = res.data
                const room = data?.rooms?.find((r: any) => r.id === roomId)
                setRoomName(room?.name || roomId || '')
                setAllPcbs(data?.pcbs || [])
                setAllRelays(data?.relays || [])
                setAllSwitches(data?.switches || [])
                setAllRooms(data?.rooms || [])
                setAllDevices(data?.devices || [])
            })
            .catch(() => toast.error('Failed to load room data'))
            .finally(() => setLoading(false))
    }, [userId, roomId])

    const pcbs = allPcbs.filter(p => p.roomId === roomId)
    const relays = allRelays.filter(r => r.roomId === roomId)
    const switches = allSwitches.filter(s => s.roomId === roomId)
    const devices = allDevices.filter(d => d.roomId === roomId)

    return (
        <div className="page-content">
            <ToastContainer />
            <div style={{ marginBottom: 16 }}>
                <span style={{ color: '#858D9D', fontSize: 13 }}>Room</span>
                <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 500 }}>{loading ? '...' : roomName}</span>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {VIEWS.map(v => (
                    <button
                        key={v.key}
                        className={`btn btn-sm ${activeView === v.key ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveView(v.key)}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {activeView === 'modules' && (
                <ModulesView
                    pcbs={pcbs}
                    allPcbs={allPcbs}
                    allRelays={allRelays}
                    allRooms={allRooms}
                    allSwitches={allSwitches}
                    allDevices={allDevices}
                    userId={userId}
                    placeId={placeId}
                    loading={loading}
                />
            )}
            {activeView === 'appliances' && (
                <AppliancesView
                    devices={devices}
                    switches={allSwitches}
                    relays={allRelays}
                    pcbs={allPcbs}
                    userId={userId}
                    placeId={placeId}
                    loading={loading}
                />
            )}
            {activeView === 'manual-controls' && (
                <ManualControlsView
                    switches={switches}
                    devices={allDevices}
                    relays={allRelays}
                    pcbs={allPcbs}
                    userId={userId}
                    placeId={placeId}
                    loading={loading}
                />
            )}
        </div>
    )
}

export default RoomPage
